import { useEffect, useState } from "react";
import api, { errMsg, formatDate } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";

// Turkish-aware fold
const fold = (s) => (s || "")
  .toLocaleLowerCase("tr-TR")
  .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ç", "c")
  .replaceAll("ğ", "g").replaceAll("ü", "u").replaceAll("ö", "o")
  .replaceAll("â", "a").replaceAll("î", "i").replaceAll("û", "u");

/**
 * Reusable CRUD page.
 * Props:
 *  - title, subtitle, endpoint, testidPrefix
 *  - fields: [{ key, label, type: 'text'|'number'|'date'|'select'|'textarea', options?, required?, defaultValue? }]
 *  - columns: [{ key, label, render?(row), align? }]
 */
export default function CrudPage({ title, subtitle, endpoint, fields, columns, testidPrefix, extraActions }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState("");

  const q = fold(search.trim());
  const filteredItems = !q ? items : items.filter(row => {
    const hay = fold(Object.values(row).map(v =>
      v === null || v === undefined ? "" :
      typeof v === "object" ? "" : String(v)
    ).join(" "));
    return hay.includes(q);
  });

  const emptyForm = () => {
    const f = {};
    fields.forEach((fld) => { f[fld.key] = fld.defaultValue ?? (fld.type === "number" ? 0 : ""); });
    return f;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [endpoint]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (item) => {
    const f = {};
    fields.forEach((fld) => { f[fld.key] = item[fld.key] ?? (fld.type === "number" ? 0 : ""); });
    setEditing(item); setForm(f); setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    fields.forEach(f => {
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
    });
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post(endpoint, payload);
        toast.success("Kayıt oluşturuldu");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={subtitle}
        testid={`${testidPrefix}-header`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {items.length > 0 && (
              <ExportButtons
                filename={testidPrefix || "rapor"}
                title={title}
                subtitle={subtitle}
                sections={[{
                  name: title, heading: title,
                  columns: columns.map(c => ({
                    key: c.key,
                    label: c.label,
                    format: c.exportFormat,
                    get: c.render ? (row) => {
                      const v = row[c.key];
                      if (v === undefined || v === null) return "";
                      if (typeof v === "number") return v;
                      return typeof v === "string" ? v : String(v);
                    } : undefined,
                  })),
                  rows: items,
                }]}
              />
            )}
            {extraActions}
            <button
              data-testid={`${testidPrefix}-add-btn`}
              onClick={openCreate}
              className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Kayıt
            </button>
          </div>
        }
      />

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden" data-testid={`${testidPrefix}-table`}>
        {items.length > 0 && (
          <div className="px-4 py-2.5 border-b border-[#F5F5F5] bg-[#FAFAFA] flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
              <input
                data-testid={`${testidPrefix}-search`}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Sayfada ara... (Türkçe uyumlu)"
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none"
              />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 num">
              {search ? `${filteredItems.length}/${items.length}` : `${items.length}`} kayıt
            </span>
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-neutral-500 text-sm">Yükleniyor...</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-neutral-400 text-sm mb-4">Henüz kayıt bulunmuyor.</div>
            <button
              onClick={openCreate}
              className="text-xs uppercase tracking-widest font-semibold text-[#0020C2] hover:underline"
            >İlk kaydı oluştur →</button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 text-sm">
            &quot;<span className="font-semibold">{search}</span>&quot; için sonuç yok
          </div>
        ) : (
          <div className="overflow-x-auto data-table">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className={`px-4 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${c.align === "right" ? "text-right" : "text-left"}`}>
                      {c.label}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((row) => (
                  <tr key={row.id} className="border-b border-[#F5F5F5]" data-testid={`${testidPrefix}-row-${row.id}`}>
                    {columns.map(c => (
                      <td key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right num" : ""}`}>
                        {c.render ? c.render(row) : (row[c.key] ?? "-")}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`${testidPrefix}-edit-${row.id}`} onClick={() => openEdit(row)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`${testidPrefix}-delete-${row.id}`} onClick={() => remove(row)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid={`${testidPrefix}-form-modal`}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{editing ? "Düzenle" : "Yeni"}</div>
                <div className="font-heading font-bold text-lg">{title}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map((fld) => (
                <div key={fld.key} className={fld.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">{fld.label}{fld.required && " *"}</label>
                  {fld.type === "textarea" ? (
                    <textarea
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                      required={fld.required}
                      value={form[fld.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                      rows={3}
                      className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none text-sm"
                    />
                  ) : fld.type === "select" ? (
                    <select
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                      required={fld.required}
                      value={form[fld.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                      className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none text-sm"
                    >
                      <option value="">Seçiniz</option>
                      {(fld.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input
                      data-testid={`${testidPrefix}-field-${fld.key}`}
                      type={fld.type}
                      step={fld.type === "number" ? "any" : undefined}
                      required={fld.required}
                      value={form[fld.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [fld.key]: e.target.value })}
                      className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none text-sm"
                    />
                  )}
                </div>
              ))}
              <div className="md:col-span-2 flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid={`${testidPrefix}-submit-btn`} type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors">{editing ? "Kaydet" : "Oluştur"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export { formatDate };
